import { useCallback, useMemo, useState } from "react";

import { api, type ApiError } from "./client";
import type { ApiNotification } from "./types";
import { useResource } from "./useResource";
import type { NotificationKind } from "../types";

// Bell view model.
export type BellNotification = {
  readonly id: string;
  readonly kind: NotificationKind;
  readonly read: boolean;
  readonly repoId: string | null;
  readonly t: number;
  /** `T-441` — shown in the row and used to route to it. */
  readonly taskLabel: string | null;
  readonly text: string;
};

// The server derives the display group (notifications.md §Display grouping);
// the client only translates its snake_case enum to the bell's kebab-case one
// rather than re-implementing the kind→group mapping.
const GROUP_TO_KIND: Record<ApiNotification["group"], NotificationKind> = {
  done: "done",
  needs_input: "needs-input",
  review: "review",
  system: "system",
};

const mapNotification = (
  row: ApiNotification,
  locallyRead: ReadonlySet<string>,
): BellNotification => ({
  id: row.public_id,
  // The map is total over the groups this client knows about, which is why the
  // lookup types as always finding one. `row.group` is whatever the response
  // put there, so a group added server-side lands on "system" rather than
  // undefined.
  // eslint-disable-next-line tinsmith/no-unnecessary-nullish-coalescing
  kind: GROUP_TO_KIND[row.group] ?? "system",
  read: row.read_at !== null || locallyRead.has(row.public_id),
  repoId: row.ref_repo_public_id,
  t: row.created_at ? Date.parse(row.created_at) : Date.now(),
  taskLabel: row.ref_task_display_id,
  text: row.subject,
});

const markReadApi = (ids: ReadonlyArray<string>): Promise<unknown> =>
  api.post("/notifications/mark-read", { ids });

const markAllReadApi = (): Promise<unknown> =>
  api.post("/notifications/mark-all-read", {});

export type UseNotifications = {
  readonly error: ApiError | undefined;
  readonly loading: boolean;
  readonly markAllRead: () => void;
  readonly markRead: (id: string) => void;
  readonly notifications: ReadonlyArray<BellNotification>;
  readonly refetch: () => void;
  readonly unreadCount: number;
};

export const useNotifications = (): UseNotifications => {
  const { data, error, loading, refetch } = useResource<
    ReadonlyArray<ApiNotification>
  >("/notifications", { limit: 50 });
  // The badge counts *all* unread (the list is capped at 50), so it comes from
  // the dedicated server counter rather than the loaded page.
  const { data: countData, refetch: refetchCount } = useResource<{
    unread_count: number;
  }>("/notifications/unread-count");
  // Optimistic read overlay so the badge/dot update instantly; a later refetch
  // (or realtime, once wired) reconciles with the server's `read_at`.
  const [locallyRead, setLocallyRead] = useState<ReadonlySet<string>>(
    new Set(),
  );

  const notifications = useMemo(
    () => (data ?? []).map((n) => mapNotification(n, locallyRead)),
    [data, locallyRead],
  );
  // Server count minus the still-unreconciled local reads: subtract only rows
  // that were unread server-side and we've optimistically marked read here.
  const unreadCount = useMemo(() => {
    const serverUnread = countData?.unread_count ?? 0;
    const pending = (data ?? []).filter(
      (n) => n.read_at === null && locallyRead.has(n.public_id),
    ).length;
    return Math.max(0, serverUnread - pending);
  }, [countData, data, locallyRead]);

  const refetchAll = useCallback((): void => {
    refetch();
    refetchCount();
  }, [refetch, refetchCount]);

  const markRead = useCallback((id: string): void => {
    setLocallyRead((prev) => new Set(prev).add(id));
    void markReadApi([id]).catch(() => {
      // Best-effort; overlay keeps the UI consistent regardless.
    });
  }, []);

  const markAllRead = useCallback((): void => {
    setLocallyRead((prev) => {
      const next = new Set(prev);
      for (const n of data ?? []) {
        next.add(n.public_id);
      }
      return next;
    });
    void markAllReadApi()
      .then(() => refetchCount())
      .catch(() => {
        // Best-effort.
      });
  }, [data, refetchCount]);

  return {
    error,
    loading,
    markAllRead,
    markRead,
    notifications,
    refetch: refetchAll,
    unreadCount,
  };
};
